import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Users, Check, X, Trash2, Plus, Save, Shield, Search, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, formatApiError, userPhotoUrl, waLink, ROLE_LABELS, STAFF_ROLES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import UserProfileDialog from "@/components/UserProfileDialog";

export default function UsersManager() {
  const [users, setUsers] = useState([]);
  const [newU, setNewU] = useState({ name: "", phone: "", instagram: "", email: "" });
  const [editing, setEditing] = useState(null); // user_id
  const [draft, setDraft] = useState({});
  const [subEmail, setSubEmail] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("az");
  const [viewId, setViewId] = useState(null);
  const [promote, setPromote] = useState({ id: "", role: "subadmin" });
  const closeView = useCallback(() => setViewId(null), []);
  const { user: me } = useAuth();
  const isMainAdmin = (me?.perms || []).includes("usuarios");
  const canRoles = (me?.perms || []).includes("cargos");
  const canDelete = (me?.perms || []).includes("excluir");

  const grantSub = async () => {
    if (!subEmail.trim()) return toast.error("Informe o email Google.");
    try {
      await api.post("/admin/subadmins", { name: "", email: subEmail.trim() });
      setSubEmail("");
      await load();
      toast.success("Subadministrador adicionado!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data);
    } catch (e) {
      /* ignore */
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, body, msg) => {
    try {
      await api.patch(`/admin/users/${id}`, body);
      await load();
      if (msg) toast.success(msg);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addUser = async () => {
    if (!newU.name.trim()) return toast.error("Informe o nome.");
    try {
      await api.post("/admin/users", newU);
      setNewU({ name: "", phone: "", instagram: "", email: "" });
      await load();
      toast.success("Usuário criado!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir este usuário?")) return;
    try { await api.delete(`/admin/users/${id}`); await load(); toast.success("Usuário excluído."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startEdit = (u) => { setEditing(u.user_id); setDraft({ name: u.name, phone: u.phone, instagram: u.instagram, email: u.email, notes: u.notes }); };
  const saveEdit = async (id) => { await patch(id, draft, "Dados salvos."); setEditing(null); };

  const pendingCount = users.filter((u) => !u.approved).length;

  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = users.filter((u) => !term || [u.name, u.email, u.phone, u.instagram].some((f) => (f || "").toLowerCase().includes(term)));
    if (sort === "recent") return list;
    const sorted = [...list].sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR", { sensitivity: "base" }));
    return sort === "za" ? sorted.reverse() : sorted;
  }, [users, q, sort]);

  return (
    <div className="mt-8" data-testid="users-manager">
      <div className="flex items-center gap-2">
        <Users className="h-5 w-5 text-[#1B7A45]" />
        <h2 className="font-display text-xl font-bold tracking-tight text-slate-900">Usuários</h2>
        {pendingCount > 0 && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-700">{pendingCount} aguardando</span>}
      </div>
      <p className="mt-1 text-sm text-slate-500">Aprove novos cadastros e edite os dados de qualquer usuário.</p>

      {/* Create manual user */}
      <div className="mt-3 grid grid-cols-1 gap-2 rounded-2xl border border-slate-200 bg-white p-3 sm:grid-cols-[1fr_1fr_1fr_1fr_auto]">
        <Input data-testid="newuser-name" placeholder="Nome" value={newU.name} onChange={(e) => setNewU({ ...newU, name: e.target.value })} className="rounded-xl" />
        <Input data-testid="newuser-phone" placeholder="WhatsApp" value={newU.phone} onChange={(e) => setNewU({ ...newU, phone: e.target.value })} className="rounded-xl" />
        <Input data-testid="newuser-instagram" placeholder="@instagram" value={newU.instagram} onChange={(e) => setNewU({ ...newU, instagram: e.target.value })} className="rounded-xl" />
        <Input data-testid="newuser-email" placeholder="Email (opcional)" value={newU.email} onChange={(e) => setNewU({ ...newU, email: e.target.value })} className="rounded-xl" />
        <Button data-testid="newuser-add" onClick={addUser} className="rounded-xl bg-[#1B7A45] text-white hover:bg-[#156337]"><Plus className="h-4 w-4" /></Button>
      </div>

      {canRoles && (
        <div data-testid="staff-manager" className="mt-2 rounded-2xl border border-[#E8611A]/30 bg-orange-50/40 p-3">
          <p className="flex items-center gap-1.5 font-display text-base font-bold text-slate-900"><Shield className="h-4 w-4 text-[#E8611A]" /> Equipe de administração</p>
          <p className="text-xs text-slate-500">Defina o cargo de cada funcionário (Admin, Subadmin, Bar, Professor, Admin Torneio). O que cada cargo pode fazer é definido em Permissões. Se alguém deixar de ser funcionário, mude para "Usuário".</p>
          <div className="mt-2 space-y-1.5">
            {users.filter((u) => STAFF_ROLES.includes(u.role)).map((u) => {
              const owner = (u.email || "").toLowerCase() === "esportesdubal@gmail.com";
              return (
                <div key={u.user_id} data-testid={`staff-row-${u.user_id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white px-3 py-2">
                  <span className="min-w-0 truncate text-sm font-semibold text-slate-800">{u.name || u.email} <span className="text-xs font-normal text-slate-400">{u.email}</span></span>
                  {owner ? <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-bold text-white">Proprietário</span> : (
                    <select data-testid={`staff-role-${u.user_id}`} value={u.role} onChange={(e) => patch(u.user_id, { role: e.target.value }, e.target.value === "user" ? "Acesso de administração removido." : "Cargo atualizado.")} className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm">
                      {Object.entries(ROLE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}<option value="user">Usuário (remover acesso)</option>
                    </select>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto]">
            <select data-testid="staff-pick-user" value={promote.id} onChange={(e) => setPromote({ ...promote, id: e.target.value })} className="w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
              <option value="">Escolher usuário para promover...</option>
              {[...users].filter((u) => u.role === "user" || !u.role).sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR")).map((u) => <option key={u.user_id} value={u.user_id}>{u.name} {u.email ? `— ${u.email}` : ""}</option>)}
            </select>
            <select data-testid="staff-pick-role" value={promote.role} onChange={(e) => setPromote({ ...promote, role: e.target.value })} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
              {Object.entries(ROLE_LABELS).filter(([k]) => k !== "admin").concat([["admin", "Admin"]]).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <Button data-testid="staff-promote" onClick={() => { if (!promote.id) return toast.error("Escolha um usuário."); patch(promote.id, { role: promote.role, approved: true }, "Cargo atribuído!"); setPromote({ id: "", role: "subadmin" }); }} className="rounded-xl bg-[#E8611A] text-white hover:bg-[#CF5212]">Promover</Button>
          </div>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto]">
            <Input data-testid="subadmin-email" placeholder="Ou email Google de quem ainda não entrou no app" value={subEmail} onChange={(e) => setSubEmail(e.target.value)} className="rounded-xl bg-white" />
            <Button data-testid="subadmin-add" onClick={grantSub} variant="outline" className="rounded-xl"><Shield className="mr-1.5 h-4 w-4" /> Dar acesso de subadmin</Button>
          </div>
        </div>
      )}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="users-search" placeholder="Pesquisar por nome, email, WhatsApp ou @" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-xl pl-9" />
        </div>
        <div className="flex gap-1.5">
          {[["az", "A-Z"], ["za", "Z-A"], ["recent", "Recentes"]].map(([id, label]) => (
            <button key={id} data-testid={`users-sort-${id}`} onClick={() => setSort(id)} className={`rounded-full px-3 py-2 text-xs font-semibold transition-colors ${sort === id ? "bg-[#1B7A45] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{label}</button>
          ))}
        </div>
      </div>
      <p data-testid="users-count" className="mt-2 text-xs text-slate-400">{shown.length} usuário(s)</p>

      <div className="mt-2 space-y-2">
        {shown.map((u) => (
          <div key={u.user_id} data-testid={`user-row-${u.user_id}`} className="rounded-2xl border border-slate-200 bg-white p-3">
            <div className="flex flex-wrap items-center gap-3">
              <button data-testid={`user-photo-${u.user_id}`} onClick={() => setViewId(u.user_id)} title="Ver perfil" className="shrink-0 rounded-full ring-2 ring-transparent transition hover:ring-[#1B7A45]">
                {userPhotoUrl(u) ? <img src={userPhotoUrl(u)} alt="" className="h-10 w-10 rounded-full object-cover" /> : <span className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 font-bold text-slate-500">{(u.name || "?")[0]}</span>}
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <button data-testid={`user-name-${u.user_id}`} onClick={() => setViewId(u.user_id)} className="font-display text-base font-bold text-slate-900 hover:text-[#1B7A45]">{u.name || "—"}</button>
                  {STAFF_ROLES.includes(u.role) && <span data-testid={`user-role-badge-${u.user_id}`} className="inline-flex items-center gap-1 rounded-full bg-[#E8611A]/10 px-2 py-0.5 text-xs font-bold text-[#E8611A]"><Shield className="h-3 w-3" /> {ROLE_LABELS[u.role]}</span>}
                  {u.manual && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">local</span>}
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${u.approved ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{u.approved ? "aprovado" : "pendente"}</span>
                </div>
                <p className="truncate text-xs text-slate-500">
                  {u.email} · {waLink(u.phone) ? <a data-testid={`user-wa-${u.user_id}`} href={waLink(u.phone)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 font-semibold text-[#1B7A45] hover:underline"><MessageCircle className="h-3 w-3" />{u.phone}</a> : "sem telefone"} · {u.instagram || "—"}
                </p>
              </div>
              {isMainAdmin && (<div className="flex shrink-0 gap-1.5">
                {!u.approved && <Button data-testid={`approve-${u.user_id}`} onClick={() => patch(u.user_id, { approved: true }, "Usuário aprovado!")} size="sm" className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700"><Check className="h-4 w-4" /></Button>}
                {u.approved && u.role !== "admin" && <Button data-testid={`revoke-${u.user_id}`} onClick={() => patch(u.user_id, { approved: false }, "Aprovação removida.")} size="sm" variant="outline" className="rounded-full border-amber-200 text-amber-700"><X className="h-4 w-4" /></Button>}
                <Button data-testid={`edit-${u.user_id}`} onClick={() => (editing === u.user_id ? setEditing(null) : startEdit(u))} size="sm" variant="outline" className="rounded-full border-slate-200">Editar</Button>
                {canRoles && u.role !== "admin" && <Button data-testid={`makeadmin-${u.user_id}`} onClick={() => patch(u.user_id, { role: "admin", approved: true }, "Agora é admin.")} size="sm" variant="outline" className="rounded-full border-slate-200"><Shield className="h-4 w-4" /></Button>}
                {canDelete && <Button data-testid={`deluser-${u.user_id}`} onClick={() => del(u.user_id)} size="sm" variant="outline" className="rounded-full border-red-200 bg-red-50 text-red-600"><Trash2 className="h-4 w-4" /></Button>}
              </div>)}
            </div>
            {editing === u.user_id && (
              <div className="mt-3 grid grid-cols-1 gap-2 border-t border-slate-100 pt-3 sm:grid-cols-4">
                <Input data-testid={`edit-name-${u.user_id}`} placeholder="Nome" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="rounded-xl" />
                <Input data-testid={`edit-phone-${u.user_id}`} placeholder="WhatsApp" value={draft.phone || ""} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} className="rounded-xl" />
                <Input data-testid={`edit-instagram-${u.user_id}`} placeholder="@instagram" value={draft.instagram || ""} onChange={(e) => setDraft({ ...draft, instagram: e.target.value })} className="rounded-xl" />
                <Input placeholder="Observações" value={draft.notes || ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} className="rounded-xl" />
                <Button data-testid={`edit-save-${u.user_id}`} onClick={() => saveEdit(u.user_id)} className="rounded-xl bg-[#1B7A45] text-white hover:bg-[#156337] sm:col-span-4"><Save className="mr-1.5 h-4 w-4" /> Salvar dados</Button>
              </div>
            )}
          </div>
        ))}
      </div>
      <UserProfileDialog userId={viewId} onClose={closeView} />
    </div>
  );
}
